import {ReputationService} from '../../shared/reputation/reputation.service';
import {Injectable} from '@angular/core';
import {NotificationRestService} from "../../api/services/notification.rest.service";
import {BehaviorSubject, defer, EMPTY, Observable, of, Subject} from "rxjs";
import {NotificationInterface} from "../../shared/interfaces/notification.interface";
import {finalize, map, mergeMap, shareReplay, takeUntil, tap} from "rxjs/operators";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {CurrentUserViewModel} from "../models/current-user.view-model";
import {NotificationApiModel} from "../../api/models/notification.api-model";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {EventSourceService} from "./event-source.service";
import {EventSourceInterface} from "../interfaces/event-source.interface";
import {TokenDataProviderService} from "./token-data-provider.service";
import {MediaObjectRestService} from "../../api/services/media-object.rest.service";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {MediaObjectApiModel} from "../../api/models/media-object.api-model";

@Injectable({
  providedIn: 'root'
})
export class NotificationDataProviderService {

  public notifications: ResultListApiModel<NotificationInterface> = new ResultListApiModel<NotificationInterface>(NotificationApiModel);
  private inFlightCollections = new Map<string, Observable<ResultListApiModel<NotificationInterface>>>();
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public readonly unreadCount$ = this.unreadCountSubject.asObservable();
  private unreadCountRequest$: Observable<number> | null = null;
  private readonly sessionReset = new Subject<void>();
  public readonly sessionReset$ = this.sessionReset.asObservable();
  private sessionGeneration = 0;
  private readonly notificationEvents = new Subject<EventSourceInterface<NotificationInterface>>();
  public readonly events$ = this.notificationEvents.asObservable();

  constructor(
    private readonly notificationRS: NotificationRestService,
    private readonly eventSourceService: EventSourceService,
    private readonly tokenDataProviderService: TokenDataProviderService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly reputation: ReputationService,
  ) {
  }


  public getCollection(page: number = 1, limit: number | null = null, isolated = false): Observable<ResultListApiModel<NotificationInterface>> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      return of(this.notifications);
    }
    const key = `${page}:${limit ?? 'default'}`;
    const inFlight = this.inFlightCollections.get(key);
    if (inFlight && !isolated) {
      return inFlight;
    }
    const queryParams = new QueryParamsApiModel();
    queryParams.page = page;
    if (limit !== null) {
      queryParams.limit = limit;
    }
    const generation = this.sessionGeneration;
    const read$: Observable<ResultListApiModel<NotificationInterface>> = defer(() => generation === this.sessionGeneration ? this.notificationRS.getCollection(queryParams) : EMPTY).pipe(
      mergeMap((resultListAM: ResultListApiModel<NotificationInterface>) =>
        this.hydrateFromUserAvatars(resultListAM).pipe(
          map((hydrated) => {
            resultListAM.records = hydrated;
            return resultListAM;
          })
        )
      ),
      takeUntil(this.sessionReset),
      map((resultListAM: ResultListApiModel<NotificationInterface>) => {
        if (isolated) return resultListAM;
        if (page === 1) {
          this.notifications = resultListAM;
        } else {
          this.notifications.total = resultListAM.total;
        }
        return resultListAM;
      }),
      finalize(() => {
        if (!isolated && this.inFlightCollections.get(key) === request$) this.inFlightCollections.delete(key);
      })
    );
    const request$: Observable<ResultListApiModel<NotificationInterface>> = isolated ? read$ : read$.pipe(shareReplay(1));
    if (!isolated) this.inFlightCollections.set(key, request$);
    return request$;
  }

  public massUpdateReadAt(notifications: NotificationInterface[]): Observable<ResultListApiModel<NotificationInterface>> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      return of(this.notifications);
    }
    const unreadMarkedCount = notifications.filter((notification) => notification?.readAt === null).length;
    const generation = this.sessionGeneration;
    return defer(() => generation === this.sessionGeneration
      ? this.notificationRS.massUpdateReadAt({notifications: notifications.map(e => e.id), readAt: true}) : EMPTY
    ).pipe(takeUntil(this.sessionReset), map((resultListAM:ResultListApiModel<NotificationInterface>) => {
      this.notifications = resultListAM;
      if (unreadMarkedCount > 0) {
        this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - unreadMarkedCount));
      }
      return this.notifications;
    }));
  }

  public getUnreadCount(): Observable<number> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      this.unreadCountSubject.next(0);
      return of(0);
    }

    if (this.unreadCountRequest$) {
      return this.unreadCountRequest$;
    }

    const generation = this.sessionGeneration;
    const request$: Observable<number> = defer(() => generation === this.sessionGeneration ? this.notificationRS.getUnreadCount() : EMPTY).pipe(
      takeUntil(this.sessionReset),
      tap((count) => this.unreadCountSubject.next(count)),
      finalize(() => {
        if (this.unreadCountRequest$ === request$) this.unreadCountRequest$ = null;
      }),
      shareReplay(1)
    );

    this.unreadCountRequest$ = request$;
    return request$;
  }

  public resetForSession(): void {
    this.sessionGeneration++;
    this.sessionReset.next();
    this.unreadCountRequest$ = null;
    this.unreadCountSubject.next(0);
    this.inFlightCollections.clear();
    this.notifications = new ResultListApiModel<NotificationInterface>(NotificationApiModel);
  }

  /** Only UserRealtimeService owns this subscription; views observe events$. */
  public setEventSource(user: CurrentUserViewModel): Observable<EventSourceInterface<NotificationInterface>> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      return EMPTY;
    }
    return this.eventSourceService.listenSingleTopic<NotificationInterface>(`/users/${user.id}/notifications`).pipe(takeUntil(this.sessionReset), tap((es: EventSourceInterface<NotificationInterface>) => {
      this.applyEvent(es);
      this.notificationEvents.next(es);
    }));
  }

  private applyEvent(es: EventSourceInterface<NotificationInterface>): void {
    const record = es.payload;
    const records = this.notifications.records || [];
    switch (es.state) {
      case 'persisted':
        if (record?.event === 'reputation_rank' || record?.event === 'reputation_badge') {
          this.reputation.invalidate();
        }
        if (!records.find((item) => item.id === record.id)) {
          this.notifications.records = [record, ...records];
          this.notifications.total++;
          if (record?.readAt === null) {
            this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
          }
        }
        break;
      case 'removed':
        this.notifications.records = records.filter((item) => item.id !== record.id);
        this.notifications.total = Math.max(0, this.notifications.total - 1);
        if (record?.readAt === null) {
          this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
        }
        break;
    }
  }

  private hydrateFromUserAvatars(recordsList: ResultListApiModel<NotificationInterface>): Observable<NotificationInterface[]> {
    const records = recordsList.records || [];
    const avatarRefs = records
      .map((notification) => notification?.fromUser?.avatar)
      .filter((avatar) => !!avatar);

    const avatarIds = Array.from(new Set(avatarRefs
      .map((avatar: any) => this.resolveMediaId(avatar))
      .filter((id) => !!id)));

    if (!avatarIds.length) {
      return of(records);
    }

    const params = new QueryParamsApiModel();
    params.filters = avatarIds.map((id) => new FilterParameterApiModel('id', id));

    return this.mediaObjectRS.getMediaObjectCollection(params).pipe(
      map((mediaResult: ResultListApiModel<MediaObjectApiModel>) => {
        const mediaById = new Map<string, MediaObjectApiModel>();
        (mediaResult.records || []).forEach((media) => {
          const byId = media?.id ? String(media.id) : null;
          const byIri = media?.['@id'] ? String(media['@id']) : null;
          if (byId) {
            mediaById.set(byId, media);
          }
          if (byIri) {
            mediaById.set(byIri, media);
          }
        });

        return records.map((notification) => {
          const avatarRef: any = notification?.fromUser?.avatar;
          const resolved = this.resolveMediaId(avatarRef);
          if (!resolved || !notification?.fromUser) {
            return notification;
          }
          const media = mediaById.get(resolved);
          if (!media) {
            return notification;
          }
          notification.fromUser.avatar = media as any;
          return notification;
        });
      })
    );
  }

  private resolveMediaId(avatar: any): string | null {
    if (!avatar) {
      return null;
    }
    if (typeof avatar === 'string') {
      const parts = avatar.split('/');
      return parts[parts.length - 1] || null;
    }
    if (avatar.id !== undefined && avatar.id !== null) {
      return String(avatar.id);
    }
    if (avatar['@id']) {
      const parts = String(avatar['@id']).split('/');
      return parts[parts.length - 1] || null;
    }
    return null;
  }
}
